import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useConsignmentInvoices } from '../api/hooks';
import { BalanceCell } from './BalanceCell';
import type { ConsignmentInvoiceRow, ShipmentState } from '../api/types';
import { shipmentStateLabel } from './shipment-state';
import { PAYABLE_TONE, payableStatusKey } from './despatch-payable';
import { Alert, Button, Chip, Empty, Money, PageHeader, Panel, Spinner, Table, type Tone } from '../ui';

const SHIPMENT_TONE: Record<ShipmentState, Tone> = {
  DRAFT: 'them', CONFIRMED: 'us', PACKED: 'us', AWAITING_PAYMENT: 'stop', DISPATCHED: 'run', DELIVERED: 'done', CANCELLED: 'lapse',
};

/**
 * A-18 — the Consignment Invoice list (docs/plans/consignment-invoice-parent §7).
 * Shipments are created inside an invoice, so this replaces the old shipment list.
 */
export function ConsignmentInvoices() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useConsignmentInvoices();
  const navigate = useNavigate();

  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;

  return (
    <>
      <PageHeader
        title="Consignment invoices"
        actions={<Button onClick={() => void navigate('/consignment-invoices/new')}>Create Consignment Invoice</Button>}
      />
      <Panel>
        <Table<ConsignmentInvoiceRow>
          className="mk-pi-list-t"
          rows={data ?? []}
          rowKey={(r) => r.id}
          empty={(
            <Empty
              title="No consignment invoices yet"
              body="Group the orders that are ready into a consignment invoice, then create the shipment from it."
              action={<Button onClick={() => void navigate('/consignment-invoices/new')}>Create Consignment Invoice</Button>}
            />
          )}
          columns={[
            { header: 'Consignment invoice', render: (r) => <Link to={`/consignment-invoices/${r.id}`}>{r.reference}</Link> },
            { header: 'Company', render: (r) => <span title={r.retailer.name}>{r.retailer.name}</span> },
            { header: 'Orders', render: (r) => r.orderReferences.join(', ') },
            // Already on the row and worth a column now that the table is no longer stretched thin.
            { header: 'Created', render: (r) => new Date(r.createdAt).toLocaleDateString() },
            /* Two columns, because they are two different things: what was sent (consigned, so
               not a debt — SH-06) and what is payable on the despatch (the courier charge, and
               only that — SH-13). One column headed "Total amount" carried their sum. */
            { header: t('despatch.colConsigned'), numeric: true, render: (r) => <Money amount={r.consignedValue} currency={r.currency} /> },
            { header: t('despatch.colPayable'), numeric: true, render: (r) => <Money amount={r.totalPayable} currency={r.currency} /> },
            {
              header: 'Payment Status',
              render: (r) => (r.state === 'CANCELLED'
                ? <Chip tone="lapse">Cancelled</Chip>
                : <Chip tone={PAYABLE_TONE[r.paymentStatus]}>{t(payableStatusKey(r.paymentStatus))}</Chip>),
            },
            {
              header: t('despatch.colStillToPay'), numeric: true,
              render: (r) => <BalanceCell balance={r.balance} currency={r.currency} />,
            },
            {
              header: 'Shipment',
              render: (r) => (r.shipment
                ? (
                  <span>
                    {r.shipment.reference}{' '}
                    <Chip tone={SHIPMENT_TONE[r.shipment.state]}>{shipmentStateLabel(r.shipment.state)}</Chip>
                  </span>
                )
                : <span className="mk-dim">—</span>),
            },
          ]}
        />
      </Panel>
    </>
  );
}

